import { CATALOG } from './catalogData.js'

// Local stand-ins so the page still has a picture when the catalog image is missing.
// Live product and variant images replace these.
const POOL_IMAGES = {
	'bpiab-personal': '/images/Personal-Pool-0129.jpg',
	'bpiab-pro2': '/images/category-image-water-birth-birth-pool-box-pools-0129.jpg',
	'bpiab-orig-pro': '/images/category-image-water-birth-birth-pool-box-pools-0129.jpg',
	'earthside-ultra': '/images/big-white-pool-0129.jpg',
	earthside: '/images/earthside-0129.jpg',
	'la-bassine': '/images/category-image-water-birth-la-bassine-pools-0129.jpg',
	'la-bassine-pro': '/images/category-image-water-birth-la-bassine-pools-0129.jpg',
	'la-bassine-maxi': '/images/category-image-water-birth-la-bassine-pools-0129.jpg',
	'la-bassine-maxi-pro': '/images/category-image-water-birth-la-bassine-pools-0129.jpg',
	'mia-via-tranquility': '/images/mia-via-pool-0129.jpg',
	'mia-via-serenity': '/images/mia-via-pool-0129.jpg',
	'oasis-elite-grand': '/images/oasis-birth-pools.png',
	'oasis-elite': '/images/oasis-birth-pools.png',
	'oasis-oval': '/images/oasis-birth-pools.png',
	'oasis-round': '/images/oasis-birth-pools.png',
	'tubina-wild': '/images/big-white-pool-0129.jpg',
}

const ACCESSORY_IMAGES = {
	hose: '/images/category-image-water-birth-accessories-0129.jpg',
	adapter: '/images/category-image-water-birth-accessories-0129.jpg',
	drain: '/images/category-image-water-birth-accessories-0129.jpg',
	airpump: '/images/category-image-water-birth-accessories-0129.jpg',
	thermo: '/images/category-image-water-birth-accessories-0129.jpg',
	net: '/images/water-birth-net-0129.jpg',
	flash: '/images/category-image-water-birth-accessories-0129.jpg',
	mirrorA: '/images/category-image-water-birth-accessories-0129.jpg',
	mirrorO: '/images/category-image-water-birth-accessories-0129.jpg',
	gloveS: '/images/water-birth-gloves-0129.jpg',
	gloveN: '/images/water-birth-gloves-0129.jpg',
	floorP: '/images/plastic-covering-removebg-preview-0129.jpg',
	floorV: '/images/category-image-water-birth-accessories-0129.jpg',
	floorE: '/images/category-image-water-birth-accessories-0129.jpg',
	lights: '/images/category-image-water-birth-accessories-0129.jpg',
	coverMini: '/images/category-image-water-birth-birth-pool-box-pools-0129.jpg',
	repair: '/images/category-image-water-birth-birth-pool-box-pools-0129.jpg',
	wipes: '/images/category-image-water-birth-accessories-0129.jpg',
}

const LINER_IMAGE = '/images/category-image-water-birth-birth-pool-liners-0129.jpg'

export const COLORS = CATALOG.colors

// Product and variant ids below were confirmed against the live catalog.
// 140223 and 130223 are Mia Via liner SKUs, not product ids.
export const ID_NOTES = [
	{
		id: 'mia-via-liners',
		mismatch: false,
		client: 'The brief listed 140223 and 130223 next to the Mia Via pools. Those numbers are the liner SKUs.',
		mockup: 'The design paired both Mia Via pools with Small Oasis Generic Pool Liner, product 3022, variant 3913, SKU 2DE82824.',
		repo: 'A read-only check of the live catalog confirmed the liner records. Serenity pool product 4071 is SKU 140123. Tranquility pool product 3726 is SKU 130123.',
		used: 'Serenity uses Mia Via Serenity Birth Pool Liner, SKU 140223, product 4070, variant 6307, $52. Tranquility uses Mia Via Tranquility Birth Pool Liner, SKU 130223, product 3728, variant 4851, $55. Product 3022 is not requested.',
	},
	{
		id: 'catalog-ids-confirmed',
		mismatch: false,
		client: 'The brief names a variant id only for La Bassine Custom liner (5891) and the Universal liner (6857).',
		mockup: 'Every other liner, pool, and accessory also has a variant id in the design data.',
		repo: 'The owner confirmed every product id and variant id in this catalog exists in the live catalog with the matching SKU.',
		used: 'A live variant is used only when its BigCommerce id or SKU matches. A different variant is not substituted.',
	},
]

export const AGREED_LINER_IDS = {
	bpMini: { bcProductId: 2042 },
	bpReg: { bcProductId: 631 },
	es: { bcProductId: 3927 },
	esU: { bcProductId: 4150 },
	lb: { bcProductId: 1313, bcVariantId: 5891 },
	lbMaxi: { bcProductId: 1884 },
	mvSerenity: { bcProductId: 4070, bcVariantId: 6307 },
	mvTranquility: { bcProductId: 3728, bcVariantId: 4851 },
	oE: { bcProductId: 3136 },
	oG: { bcProductId: 3456 },
	oOv: { bcProductId: 3217 },
	oR: { bcProductId: 3396 },
	twO: { bcProductId: 4309 },
	twM: { bcProductId: 4354 },
	universal: { bcProductId: 1314, bcVariantId: 6857 },
}

function decorate() {
	const liners = {}
	for (const [key, liner] of Object.entries(CATALOG.liners)) {
		liners[key] = { ...liner, image: LINER_IMAGE }
	}
	const pools = CATALOG.pools.map((pool) => ({
		...pool,
		features: pool.features.slice(),
		specs: pool.specs,
		variants: pool.variants.map((variant) => ({
			...variant,
			image: POOL_IMAGES[pool.id] || LINER_IMAGE,
		})),
	}))
	const accessories = CATALOG.accessories.map((group) => ({
		group: group.group,
		items: group.items.map((item) => {
			const image = ACCESSORY_IMAGES[item.id] || '/images/category-image-water-birth-accessories-0129.jpg'
			if (item.options) {
				return { ...item, image, options: item.options.map((option) => ({ ...option, image })) }
			}
			return { ...item, image }
		}),
	}))
	return { liners, pools, accessories }
}

export const REFERENCE = decorate()

export function linerForPool(pool, size) {
	if (!pool) return null
	if (pool.linerBySize) return pool.linerBySize[size] || Object.values(pool.linerBySize)[0]
	return pool.linerId
}

export function collectBcProductIds(catalog = REFERENCE) {
	const ids = new Set()
	for (const pool of catalog.pools) {
		for (const variant of pool.variants) ids.add(Number(variant.bcProductId))
	}
	for (const liner of Object.values(catalog.liners)) ids.add(Number(liner.bcProductId))
	for (const group of catalog.accessories) {
		for (const item of group.items) {
			if (item.options) {
				for (const option of item.options) ids.add(Number(option.bcProductId))
			} else {
				ids.add(Number(item.bcProductId))
			}
		}
	}
	return [...ids]
}
